import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { AskResponse } from '../types';
import AskAnswerCard from '../components/AskAnswerCard';

// Dedicated Ask surface (spec 11.5). A primary way to interrogate the whole system in plain
// language: the deterministic matcher routes the question to the screen that answers it; only an
// unmatched question (opt-in) goes to the bounded parse edge, which classifies intent only and
// never writes the answer or a number. Answers render as cards with provenance + deep links, kept
// in a running QUERY LOG — a list of asks + their answer cards, never a chat thread.

interface LogEntry { id: number; response: AskResponse }

const EXAMPLES = [
  'Can I drive higher growth than currently?',
  "What's breached right now?",
  'Where should I focus?',
  'What did we decide about aluminum?',
];

export default function Ask() {
  const [params, setParams] = useSearchParams();
  const [input, setInput] = useState('');
  const [log, setLog] = useState<LogEntry[]>([]);
  const nextId = useRef(1);

  const ask = useMutation({
    mutationFn: (q: string) => api.ask(q),
    onSuccess: (response) => setLog((prev) => [{ id: nextId.current++, response }, ...prev]),
  });
  const run = (q: string) => { const t = q.trim(); if (t) ask.mutate(t); };

  // The topbar launcher hands off via /ask?q=…&t=<nonce>; run it once per nonce, then clear the
  // params so a refresh (or a strict-mode double effect) does not re-ask.
  const qParam = params.get('q');
  const tParam = params.get('t');
  const lastNonce = useRef<string | null>(null);
  useEffect(() => {
    const q = qParam?.trim();
    if (q && tParam !== lastNonce.current) {
      lastNonce.current = tParam;
      run(q);
      const next = new URLSearchParams(params);
      next.delete('q');
      next.delete('t');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qParam, tParam]);

  function submit() {
    const q = input.trim();
    if (!q) return;
    run(q);
    setInput('');
  }

  return (
    <div>
      <div className="ph">Command Center · Ask the System</div>
      <div className="h2">Ask the system</div>
      <p className="lead">
        Ask in plain language; the system routes you to the screen that answers it. It never writes
        the answer or a number. Every figure comes from a live surface you can open.
      </p>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <input
            className="c2-input"
            style={{ flex: 1 }}
            placeholder="e.g. Can I drive higher growth than currently?"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            aria-label="Ask the system"
          />
          <button type="button" className="btn primary" onClick={submit} disabled={ask.isPending}>
            {ask.isPending ? 'Asking…' : 'Ask'}
          </button>
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              className="btn ghost"
              style={{ minHeight: 'auto', padding: '4px 10px', fontSize: 12 }}
              onClick={() => { setInput(ex); run(ex); }}
            >
              {ex}
            </button>
          ))}
        </div>
        {ask.isError && <div className="login-err" style={{ marginTop: 8 }}>Could not reach the system. Try again.</div>}
      </div>

      {/* Query log: recent asks + their answer cards, newest first. Not a chat. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ask.isPending && <div className="panel"><div className="small faint">Routing your question…</div></div>}
        {log.map((entry) => <AskAnswerCard key={entry.id} response={entry.response} />)}
        {log.length === 0 && !ask.isPending && (
          <div className="panel">
            <div className="small faint">
              Your asks appear here, each with its answer card and a link to the screen that resolves it.
              This is a query log, not a chat: the system routes and shows; it never improvises an answer.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
